import React from 'react';
import PreventOrphanText, {
  preventOrphanTextFn,
} from '../PreventOrphanText/PreventOrphanText';
import s from './Title.scss';
import { cn } from '@express-labs/raven-tools';

type childrenProps = typeof preventOrphanTextFn | string;

type BaseTitleProps<C extends React.ElementType> = {
  /** When true, the text is capitalized. */
  capitalize?: boolean;
  /** Text to use as the title */
  children: childrenProps;
  /** Optional custom class name(s) to further customize the title. */
  className?: string;
  /** When true, the Title component prevents a word from being displayed alone on a line by itself.  There will always be at least two words on a line. Resize the browser window in Storybook to see this in action. */
  preventOrphans?: boolean;
  /** Valid values are h1, h2, h3, h4, h5 */
  tag?: C;
  /** unique identifier set by content stack */
  uid?: string;
};

export type TitleProps<C extends React.ElementType> = React.PropsWithChildren<
  BaseTitleProps<C>
>;

class Title extends React.Component<
  TitleProps<'h1' | 'h2' | 'h3' | 'h4' | 'h5'>
> {
  public static displayName = 'Title';

  static headerClassMap = {
    h1: 'header1',
    h2: 'header2',
    h3: 'header3',
    h4: 'header4',
    h5: 'header5',
  };

  render() {
    const {
      tag: Tag = 'h1',
      capitalize,
      className,
      children,
      preventOrphans,
      uid,
      ...attrs
    } = this.props;

    return (
      <Tag
        className={cn(
          s.title,
          Title.headerClassMap[Tag],
          capitalize && s.capitalize,
          className
        )}
        data-uid={uid}
        {...attrs}
      >
        {preventOrphans ? (
          <PreventOrphanText n={2}>{children}</PreventOrphanText>
        ) : (
          children
        )}
      </Tag>
    );
  }
}

export default Title;
